import React from "react";
import { observer } from "mobx-react";

import LazyMarkdownRenderer from "./LazyMarkdownRenderer.tsx";

type StreamingMarkdownProps = {
  content: string;
  isGenerating: boolean;
  enableFormatting: boolean;
};

/**
 * Boxed, height-capped panel that renders live-streamed tool output
 * (sub-agent markdown, audio transcription) with full markdown support
 * and auto-scroll to the newest content.
 */
@observer
export class StreamingMarkdown extends React.Component<StreamingMarkdownProps> {
  private _scrollRef = React.createRef<HTMLDivElement>();
  private _prevContent: string = '';
  private _prevScrollHeight: number | null = null;
  private _disableAutoScroll: boolean = false;
  private _disableAutoScrollTimeoutId: number | null = null;

  constructor(props: StreamingMarkdownProps) {
    super(props);
    this._prevContent = props.content;
  }

  private _onContentScroll = (): void => {
    const contentEl = this._scrollRef.current;
    if (!contentEl) return;
    // Only pause auto-scroll when the user scrolls away from the bottom.
    // Programmatic follow-scrolls land at the bottom — and since scroll
    // events fire asynchronously, a flag set-and-reset around the scrollTop
    // assignment can never actually catch them.
    const distanceFromBottom = contentEl.scrollHeight - contentEl.scrollTop - contentEl.clientHeight;
    if (distanceFromBottom <= 2) return;
    this._disableAutoScroll = true;
    if (this._disableAutoScrollTimeoutId) clearTimeout(this._disableAutoScrollTimeoutId);
    this._disableAutoScrollTimeoutId = window.setTimeout(() => {
      this._disableAutoScroll = false;
      this._disableAutoScrollTimeoutId = null;
    }, 1000);
  };

  private _scrollToEndIfNeeded(): void {
    const contentEl = this._scrollRef.current;
    if (!contentEl) return;

    // Measure where the user was relative to the bottom BEFORE this content
    // update: scrollTop hasn't changed yet, and the previous frame's height
    // is known. A flat pixel threshold on the post-update height breaks the
    // moment a single streamed chunk (code block, table) grows the content
    // by more than that threshold in one step.
    const prevHeight = this._prevScrollHeight ?? contentEl.scrollHeight;
    const wasNearBottom = prevHeight - contentEl.scrollTop <= contentEl.clientHeight + 40;

    this._prevScrollHeight = contentEl.scrollHeight;
    if (!wasNearBottom || this._disableAutoScroll) return;

    // Defer to rAF so the new content is laid out first, and scroll
    // instantly — an animated scroll leaves the box mid-flight where the
    // next update can't catch up.
    requestAnimationFrame(() => {
      const el = this._scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }

  public componentDidUpdate(prevProps: StreamingMarkdownProps): void {
    if (this.props.content !== this._prevContent) {
      if (prevProps.isGenerating) {
        this._scrollToEndIfNeeded();
      }
      this._prevContent = this.props.content;
    }
  }

  public componentDidMount(): void {
    // Start at the bottom so the newest streamed content is visible
    const el = this._scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }

  public componentWillUnmount(): void {
    if (this._disableAutoScrollTimeoutId) clearTimeout(this._disableAutoScrollTimeoutId);
  }

  render() {
    const { content, isGenerating, enableFormatting } = this.props;
    return (
      <div className="tool-streaming-text">
        <div className="tool-streaming-text-scroll" ref={this._scrollRef} onScroll={this._onContentScroll}>
          <LazyMarkdownRenderer
            content={content}
            isGenerating={isGenerating}
            isThisMessageGenerating={isGenerating}
            enableFormatting={enableFormatting}
          />
        </div>
      </div>
    );
  }
}
